'use client';

import React, { useState } from 'react';
import { useFormik } from 'formik';
import 'react-international-phone/style.css';
import { PhoneInput } from 'react-international-phone';
import { Button } from '@/components/button/Button';
import Icon from '@/components/icon/Icon';
import { FormStepProps } from '@/sections/forms/progressive-form/steps/form-step/types';
import { Headline } from '@/components/headline/Headline';
import { Input } from '@/components/input/Input';
import '@/components/gateway-forms/styles.css';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';
import {
  buildLeadFormData,
  generateSeasonYears,
  getFormValidationSchema,
} from '@/sections/forms/progressive-form/steps/form-step/utils/formUtils';
import { HeadlineProps } from '@/components/headline/types';
import { useTranslations } from '@/lib/locale-context';
import { submitLeadFormData } from '@/lib/lead-submission';

const FormStep: React.FC<
  FormStepProps & {
    step: number;
    totalSteps: number;
    hiddenFields?: { key: string; value: string }[];
    onResetToFirstStep?: () => void;
    formHeadline?: HeadlineProps;
    successMessage?: HeadlineProps;
  }
> = ({
  description,
  onBack,
  step,
  totalSteps,
  callbackRequestNotes,
  hiddenFields,
  onResetToFirstStep,
  formHeadline,
  successMessage,
}) => {
  const { t } = useTranslations();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const seasonYears = React.useMemo(() => generateSeasonYears(), []);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      gender: 'Male',
      Tech_Data_Origin__c: 'W2L',
      Source_URL__c: 'https://smzh.ch/de',
      UTM_Parameter__c: '',
      strasseNummer: '',
      zip: '',
      city: '',
      seasonYear: '',
      profession: '',
    },
    validationSchema: getFormValidationSchema(t),
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      try {
        const formData = buildLeadFormData(
          values,
          callbackRequestNotes,
          hiddenFields,
        );

        const response = await submitLeadFormData(formData);

        if (!response.ok) throw new Error('Fehler beim Senden des Formulars');
        setIsSubmitted(true);
      } catch (error) {
        console.error('Submission error:', error);
        alert(t('form.tryAgain') as string);
      }
    },
  });

  const {
    values,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    isSubmitting,
    isValid,
    dirty,
    resetForm,
  } = formik;

  if (isSubmitted) {
    return (
      <div className="container mx-auto w-full rounded-lg bg-white px-8 py-6 text-center shadow-sm lg:w-2/3">
        <div className="mx-auto mb-4 flex h-fit w-fit items-center justify-center rounded-xl bg-brand-200 p-3">
          <Icon name="check" color="#07314C" size="24px" />
        </div>
        {successMessage && <Headline {...successMessage} />}
        <Button
          variant="secondary"
          name="Zurück zur Eingabe"
          onClick={() => {
            setIsSubmitted(false);
            resetForm();
            onResetToFirstStep?.();
          }}
          className="mx-auto w-fit justify-center !border-brand-700 !bg-transparent !text-brand-700"
        >
          {t('form.returnToForm')}
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto w-full rounded-lg bg-white shadow-sm lg:w-2/3">
      <div className="px-8 pt-6">
        <div className="border-b border-b-neutral-100 pb-4">
          <div className="flex items-center justify-between">
            {formHeadline && <Headline {...formHeadline} />}
            <span className="h-fit min-w-[55px] rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-600">
              {step} / {totalSteps}
            </span>
          </div>
          {description && (
            <SerializeDescription
              description={description as any}
              className="mb-4 mt-2 text-sm text-gray-500"
            />
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6 px-8 py-6">
          {/* Gender (radio) */}
          <div className="col-span-2">
            <label className="mb-2 block text-sm font-medium text-neutral-700">
              {t('form.salutation')}
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="gender"
                  value="Male"
                  checked={values.gender === 'Male'}
                  onChange={() => setFieldValue('gender', 'Male')}
                  disabled={isSubmitting}
                />
                {t('form.mr')}
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="gender"
                  value="Female"
                  checked={values.gender === 'Female'}
                  onChange={() => setFieldValue('gender', 'Female')}
                  disabled={isSubmitting}
                />
                {t('form.ms')}
              </label>
            </div>
            {touched.gender && errors.gender && (
              <p className="mt-1 text-sm text-red-600">{errors.gender}</p>
            )}
          </div>

          {hiddenFields?.map(({ key, value }) => (
            <input key={key} type="hidden" name={key} value={value} />
          ))}

          {/* First/Last/Email/Phone */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              name="firstName"
              label={t('gatewayForms.firstName')}
              placeholder={t('form.placeholderOne')}
              type="text"
              variant="light"
              value={values.firstName}
              onChange={(value) => setFieldValue('firstName', value)}
              onBlur={() => setFieldTouched('firstName', true)}
              error={touched.firstName && !!errors.firstName}
              errorMessage={touched.firstName ? errors.firstName : undefined}
              disabled={isSubmitting}
            />
            <Input
              name="lastName"
              label={t('gatewayForms.lastName')}
              placeholder={t('form.placeholderTwo')}
              type="text"
              variant="light"
              value={values.lastName}
              onChange={(value) => setFieldValue('lastName', value)}
              onBlur={() => setFieldTouched('lastName', true)}
              error={touched.lastName && !!errors.lastName}
              errorMessage={touched.lastName ? errors.lastName : undefined}
              disabled={isSubmitting}
            />
            <Input
              name="email"
              label={t('gatewayForms.email')}
              placeholder="z.B. max@example.com"
              type="email"
              variant="light"
              value={values.email}
              onChange={(value) => setFieldValue('email', value)}
              onBlur={() => setFieldTouched('email', true)}
              error={touched.email && !!errors.email}
              errorMessage={touched.email ? errors.email : undefined}
              disabled={isSubmitting}
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700">
                {t('gatewayForms.phone')}
              </label>
              <PhoneInput
                defaultCountry="ch"
                value={values.phone}
                onChange={(value) => setFieldValue('phone', value)}
                inputClassName="w-full py-6"
                className={
                  touched.phone && errors.phone ? 'border border-red-500' : ''
                }
                onBlur={() => setFieldTouched('phone', true)}
                disabled={isSubmitting}
              />
              {touched.phone && errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>
          </div>
        </div>

        {/* Strasse/Zip/City */}
        <div className="flex flex-col gap-6 px-8 pb-6 md:flex-row">
          <div className="w-full">
            <Input
              name="strasseNummer"
              variant="light"
              label={t('form.street')}
              placeholder={t('form.streetPlaceholder')}
              type="text"
              value={values.strasseNummer}
              onChange={(value) => setFieldValue('strasseNummer', value)}
              onBlur={() => setFieldTouched('strasseNummer', true)}
              error={touched.strasseNummer && !!errors.strasseNummer}
              errorMessage={
                touched.strasseNummer ? errors.strasseNummer : undefined
              }
              disabled={isSubmitting}
            />
          </div>
        </div>
        <div className="flex flex-col gap-6 px-8 pb-6 md:flex-row">
          <div className="w-full md:w-1/2">
            <Input
              name="zip"
              variant="light"
              label={t('form.zip')}
              placeholder={t('form.zipPlaceholder')}
              type="text"
              value={values.zip}
              onChange={(value) => {
                if (typeof value === 'string') {
                  setFieldValue('zip', value.replace(/\D/g, '')); // only keep digits
                } else {
                  setFieldValue('zip', '');
                }
              }}
              onBlur={() => setFieldTouched('zip', true)}
              error={touched.zip && !!errors.zip}
              errorMessage={touched.zip ? errors.zip : undefined}
              disabled={isSubmitting}
            />
          </div>
          <div className="w-full md:w-1/2">
            <Input
              name="city"
              variant="light"
              label={t('form.city')}
              placeholder={t('form.cityPlaceholder')}
              type="text"
              value={values.city}
              onChange={(value) => setFieldValue('city', value)}
              onBlur={() => setFieldTouched('city', true)}
              error={touched.city && !!errors.city}
              errorMessage={touched.city ? errors.city : undefined}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Season year / Profession */}
        <div className="flex flex-col gap-6 px-8 pb-6 md:flex-row">
          <div className="w-full md:w-1/2">
            <label className="block text-sm font-medium text-gray-700">
              {t('form.seasonYear')}
            </label>
            <select
              name="seasonYear"
              value={values.seasonYear}
              onChange={(e) => setFieldValue('seasonYear', e.target.value)}
              onBlur={() => setFieldTouched('seasonYear', true)}
              disabled={isSubmitting}
              className={`select-year mt-1 block w-full rounded-md border px-3 py-2 shadow-sm sm:text-sm ${
                touched.seasonYear && errors.seasonYear ? 'border-red-500' : ''
              }`}
            >
              <option value="">{t('form.seasonYearPlaceholder')}</option>
              {seasonYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            {touched.seasonYear && errors.seasonYear && (
              <p className="mt-1 text-sm text-red-800">{errors.seasonYear}</p>
            )}
          </div>
          <div className="w-full md:w-1/2">
            <Input
              name="profession"
              variant="light"
              label={t('form.profession')}
              placeholder={t('form.professionPlaceholder')}
              type="text"
              value={values.profession}
              onChange={(value) => setFieldValue('profession', value)}
              onBlur={() => setFieldTouched('profession', true)}
              error={touched.profession && !!errors.profession}
              errorMessage={touched.profession ? errors.profession : undefined}
              disabled={isSubmitting}
            />
          </div>
        </div>
        {/* Footer buttons */}
        <div className="flex flex-col gap-4 border-t border-t-neutral-100 px-8 py-6 md:flex-row md:justify-end">
          <Button
            variant="secondary"
            sizes="small"
            name={t('common.back')}
            leftIcon={{ name: 'chevron-left', size: '18px' }}
            onClick={onBack}
            disabled={isSubmitting}
            className="w-full justify-center rounded px-4 py-2 text-sm transition-all duration-100 ease-in-out md:w-auto"
          >
            {t('common.back')}
          </Button>
          <Button
            variant="primary"
            sizes="small"
            name={t('common.send')}
            type="submit"
            disabled={isSubmitting || !isValid || !dirty}
            className="w-full justify-center rounded px-4 py-2 text-sm text-white transition-all duration-100 ease-in-out disabled:cursor-not-allowed disabled:opacity-50 md:w-auto"
          >
            <div className="flex items-center gap-2">
              {isSubmitting && (
                <Icon name="loader" size="16px" className="animate-spin" />
              )}
              <span>
                {isSubmitting ? t('common.sending') : t('common.send')}
              </span>
            </div>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default FormStep;
